import React, { useState } from 'react';
import { useSimulator } from '../../context/SimulatorContext';
import BUSINESS_CONFIG from '../../config/businessConfig';
import { useTranslation } from '../../hooks/useTranslation';
import './OldSchoolSlotMachine.css';

const slotSymbolsList = BUSINESS_CONFIG.slotMachine.symbols;
const NUM_REELS = BUSINESS_CONFIG.slotMachine.numReels;

const LEVER_DOWN_DURATION = BUSINESS_CONFIG.slotMachine.leverDownDuration;
const LEVER_UP_DURATION = BUSINESS_CONFIG.slotMachine.leverUpDuration;
const LEVER_SPIN_TRIGGER_OFFSET = BUSINESS_CONFIG.slotMachine.leverSpinTriggerOffset;
const SPIN_DURATION = BUSINESS_CONFIG.slotMachine.spinDuration;
const WIN_PROBABILITY = BUSINESS_CONFIG.slotMachine.winProbability;
const WIN_MULTIPLIER = BUSINESS_CONFIG.slotMachine.winMultiplier;

// Helper to get a random symbol
const getRandomSymbol = () => slotSymbolsList[Math.floor(Math.random() * slotSymbolsList.length)];

const Reel = ({ symbols, isSpinning }) => {
  return (
    <div className={`reel ${isSpinning ? 'spinning' : ''}`}>
      <div className="reel-inner">
        {symbols.map((symbol, i) => (
          <div key={i} className="reel-symbol">{symbol}</div>
        ))}
      </div>
    </div>
  );
};

export default function OldSchoolSlotMachine({ onClose, containerRef = null }) {
  const { t } = useTranslation();
  const { state, actions } = useSimulator();
  const betSizes = BUSINESS_CONFIG.slotMachine.betSizes;
  const [betIndex, setBetIndex] = useState(BUSINESS_CONFIG.slotMachine.defaultBetIndex);
  const bet = betSizes[betIndex];

  const [spinning, setSpinning] = useState(false);
  const [lastWin, setLastWin] = useState(null);
  const [resultReels, setResultReels] = useState(() => [getRandomSymbol(), getRandomSymbol(), getRandomSymbol()]);
  const [leverPulled, setLeverPulled] = useState(false);

  const weeklyHours = state.weeklyPlayTime || 0;
  const balance = state.balance;
  const netResult = state.netResult;
  const week = state.currentWeek || 1;
  const playDisabled = state.limitStatus.loginReached.daily || state.limitStatus.loginReached.weekly || state.limitStatus.loginReached.monthly;

  const handleSpin = async () => {
    if (spinning || balance < bet || playDisabled) return;

    setLastWin(null);
    setLeverPulled(true); // Animate lever down

    // After leverDownDuration, animate lever up
    setTimeout(() => {
      setLeverPulled(false); // Animate lever up
      // After leverSpinTriggerOffset (while lever is returning), start reels spinning
      setTimeout(() => {
        setSpinning(true);
        // Simulate reel spinning for animation
        // Determine win/loss
        const isWin = Math.random() < WIN_PROBABILITY;
        let finalSymbols;
        let winAmount = 0;

        if (isWin) {
          const winSymbol = getRandomSymbol();
          finalSymbols = Array(NUM_REELS).fill(winSymbol);
          winAmount = bet * WIN_MULTIPLIER;
        } else {
          finalSymbols = Array(NUM_REELS).fill(null).map(() => getRandomSymbol());
          // Ensure it's not a winning combination
          while (finalSymbols.every(s => s === finalSymbols[0])) {
            finalSymbols = Array(NUM_REELS).fill(null).map(() => getRandomSymbol());
          }
          winAmount = -bet;
        }

        setTimeout(() => {
          setResultReels(finalSymbols);
          setSpinning(false);
          actions.play(1, bet, isWin);
          setLastWin(winAmount);
        }, SPIN_DURATION);
      }, LEVER_SPIN_TRIGGER_OFFSET);
    }, LEVER_DOWN_DURATION);
  };

  const getNetResultColor = () => {
    if (netResult > 0) return 'text-green-400';
    if (netResult < 0) return 'text-red-400';
    return 'text-gray-300';
  };

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50 p-4">
      <div className="slot-machine-container">
        <div className="slot-machine-body">
          <div className="slot-machine-top">
            <div className="top-logo">
              <span className="logo-text-main">{t('slot_machine.week_play', { number: week })}</span>
            </div>
          </div>

          <div className="slot-machine-screen">
            <div className="reels-window">
              {Array(NUM_REELS).fill(null).map((_, i) => (
                <Reel 
                  key={i} 
                  symbols={spinning ? [...slotSymbolsList, ...slotSymbolsList] : [resultReels[i]]} 
                  isSpinning={spinning} 
                />
              ))}
            </div>
            <div className="win-display">
              {lastWin !== null && (
                <span className={`win-text ${lastWin > 0 ? 'win' : 'lose'}`}>
                  {lastWin > 0 ? `${t('slot_machine.win')} €${lastWin}` : `${t('slot_machine.lose')} €${Math.abs(lastWin)}`}
                </span>
              )}
            </div>
            <div className="stats-display">
              <div className="stat-item">
                <span className="stat-label">{t('slot_machine.stats.balance')}</span>
                <span className="stat-value">€{balance}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">{t('slot_machine.stats.net_result')}</span>
                <span className={`stat-value ${getNetResultColor()}`}>{netResult > 0 ? '+' : ''}€{netResult}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">{t('slot_machine.stats.weekly_hours')}</span>
                <span className="stat-value">{weeklyHours}h</span>
              </div>
            </div>
          </div>
          
          <div className="slot-machine-panel">
              <div className="controls-group">
                  <button 
                    onClick={onClose} 
                    className="control-button back-button"
                    disabled={spinning}
                  >
                    {t('slot_machine.controls.back')}
                  </button>
                  <div className="bet-controls">
                    <button 
                      className="bet-button"
                      onClick={() => setBetIndex(i => Math.max(0, i - 1))}
                      disabled={betIndex === 0 || spinning}
                    >
                      -
                    </button>
                    <div className="bet-display">
                      <span className="bet-label">{t('slot_machine.controls.bet_size')}</span>
                      <span className="bet-value">€{bet}</span>
                    </div>
                    <button 
                      className="bet-button"
                      onClick={() => setBetIndex(i => Math.min(betSizes.length - 1, i + 1))}
                      disabled={betIndex === betSizes.length - 1 || spinning}
                    >
                      +
                    </button>
                  </div>
                  <button 
                    className="control-button spin-button" 
                    onClick={handleSpin}
                    disabled={balance < bet || spinning || playDisabled}
                  >
                    {t('slot_machine.controls.spin')}
                  </button>
              </div>
              <p className="info-text">{t('slot_machine.info_spin_hour')}</p>
          </div>
        </div>
        <div className={`slot-handle ${leverPulled ? 'pulled' : ''}`} onClick={handleSpin}>
          <div className="handle-ball"></div>
          <div className="handle-stick"></div>
        </div>
      </div>
    </div>
  );
} 